import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { Button, Form, Nav, Modal } from 'react-bootstrap'
import { Quill } from 'react-quill'
import _ from 'lodash'
import 'react-quill/dist/quill.snow.css'

import * as Constants from '../../helpers/constants'
import showToastNotification from '../widgets/toastify'
import { ApiHelper } from '../../helpers/apihelper'
import '../../static/css/announcement_bar.css'
import {
  TABS,
  CHAR_LIMITS,
  FONT_SIZES,
  TOOLBAR_CONTAINER,
  TOOLTIP_MAP,
  initialUserState,
  initialPublicState,
  SYSPROP_NAMES,
} from '../announcement_bar/constants'
import AppAnnouncementMessage from '../announcement_bar/AppAnnouncementMessage'
import WebsiteAnnouncementMessage from '../announcement_bar/WebsiteAnnouncementMessage'
import ConfirmationModal from '../../helpers/confirmationModal'

const SizeStyle = Quill.import('attributors/style/size')
SizeStyle.whitelist = FONT_SIZES
Quill.register(SizeStyle, true)

const LinkBlot = Quill.import('formats/link')
class CopCustomLink extends LinkBlot {
  static create(value) {
    const url = typeof value === 'string' ? value : value.url
    const node = super.create(url)
    const target = typeof value === 'object' ? value.target : '_self'
    if (target === '_blank') {
      node.setAttribute('target', '_blank')
      node.setAttribute('rel', 'noopener noreferrer')
    } else {
      node.removeAttribute('target')
      node.removeAttribute('rel')
    }
    return node
  }

  static formats(node) {
    return {
      url: node.getAttribute('href'),
      target: node.getAttribute('target') || '_self',
    }
  }

  format(name, value) {
    if (name !== 'link') return super.format(name, value)
    if (!value) return super.format(name, value)
    const url = typeof value === 'string' ? value : value.url
    this.domNode.setAttribute('href', CopCustomLink.sanitize(url))
    const target = typeof value === 'object' ? value.target : '_self'
    if (target === '_blank') {
      this.domNode.setAttribute('target', '_blank')
      this.domNode.setAttribute('rel', 'noopener noreferrer')
    } else {
      this.domNode.removeAttribute('target')
      this.domNode.removeAttribute('rel')
    }
  }
}
Quill.register(CopCustomLink, true)

// Mirrors real production defaults: cophonewebapp's app navbar bar background
// (#485957) and the ClearoutPhone marketing site's light purple gradient bar.
const DEFAULT_BG = {
  [TABS.USER]: '#485957',
  [TABS.PUBLIC]: 'linear-gradient(to right, #f5f0ff, #ece1ff, #e1d2ff)',
}
const DEFAULT_BG_PICKER_COLOR = '#485957'

const getPlainTextLength = (html) => {
  if (!html || html === '<p><br></p>') return 0
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').length
}

const toInlineHtml = (html) => {
  if (!html) return html
  return html
    .replace(/<p[^>]*>/g, '<span>')
    .replace(/<\/p>/g, '</span>')
    .replace(/<br\s*\/?>/g, '')
    .trim()
}

const CurrentlyLiveCard = ({ loading, liveData, tab, onLoad }) => {
  const isUser = tab === TABS.USER
  const message = isUser ? liveData?.message : liveData
  const hasContent = !loading && message && message !== '<p><br></p>'
  const closeBtnColor = isUser ? _.get(liveData, 'styles.close_btn.color', '') : ''
  const bgColor = isUser
    ? (_.get(liveData, 'styles.top_container.background', '') || _.get(liveData, 'styles.top_container.backgroundColor', ''))
    : ''
  const dismissable = isUser ? (liveData?.dismissable !== undefined ? liveData.dismissable : true) : false
  const defaultTextColor = isUser ? undefined : '#000000'

  return (
    <div className='announcement-current-live mb-4'>
      <div className='announcement-current-live-header'>
        <span className='announcement-live-badge'>
          <span className='announcement-live-dot' />
          Currently Live
        </span>
        {hasContent && (
          <Button 
            variant='outline-primary' 
            size='sm' 
            onClick={onLoad}
          >
            Load into Editor
          </Button>
        )}
      </div>
      {loading ? (
        <div 
          className='announcement-current-live-body text-muted' 
          style={{ fontSize: '13px' }}
        >
          Fetching current live announcement…
        </div>
      ) : hasContent ? (
        <div className='announcement-current-live-body'>
          <div className='announcement-preview-mock-header'>
            <div className='announcement-preview-mock-logo' />
            <div
              className='announcement-preview-bar'
              style={{
                background: bgColor || DEFAULT_BG[tab],
                color: defaultTextColor,
              }}
            >
              <div
                className='announcement-preview-message'
                dangerouslySetInnerHTML={{ __html: message }}
              />
              {isUser && dismissable && (
                <span
                  className='announcement-preview-close'
                  style={{ color: closeBtnColor || '#fff' }}
                >
                  ✕
                </span>
              )}
            </div>
            <div className='announcement-preview-mock-nav' />
          </div>
          {isUser && (
            <div className='announcement-current-live-settings'>
              {closeBtnColor && (
                <span className='announcement-live-setting-item'>
                  <span
                    className='announcement-live-swatch'
                    style={{ background: closeBtnColor }}
                  />
                  Close btn: {closeBtnColor}
                </span>
              )}
              {bgColor && (
                <span className='announcement-live-setting-item'>
                  <span
                    className='announcement-live-swatch'
                    style={{ background: bgColor }}
                  />
                  Background: {bgColor}
                </span>
              )}
              <span className='announcement-live-setting-item'>
                Dismissable: {dismissable ? 'Yes' : 'No'}
              </span>
            </div>
          )}
        </div>
      ) : (
        <div
          className='announcement-current-live-body text-muted'
          style={{ fontSize: '13px' }}
        >
          No announcement is currently live.
        </div>
      )}
    </div>
  )
}

const CopAnnouncementBar = ({ client }) => {
  const [activeTab, setActiveTab] = useState(TABS.USER)
  const [userState, setUserState] = useState(initialUserState)
  const [publicState, setPublicState] = useState(initialPublicState)
  const [loading, setLoading] = useState(false)
  const [linkModal, setLinkModal] = useState({ show: false, url: '', newTab: false })
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [currentLiveUser, setCurrentLiveUser] = useState(null)
  const [currentLivePublic, setCurrentLivePublic] = useState(null)
  const [loadingLive, setLoadingLive] = useState(true)
  const [userPasteError, setUserPasteError] = useState('')
  const [publicPasteError, setPublicPasteError] = useState('')
  const userQuillRef = useRef(null)
  const publicQuillRef = useRef(null)
  const userLastValidDelta = useRef(null)
  const publicLastValidDelta = useRef(null)
  const pendingLinkRef = useRef({ quillRef: null, selection: null })
  const userLinkHandlerRef = useRef(null)
  const publicLinkHandlerRef = useRef(null)

  const openLinkModal = useCallback((quillRef) => {
    const quill = quillRef.current?.getEditor()
    if (!quill) return
    let sel = quill.getSelection(true)
    if (!sel) return
    let format = quill.getFormat(sel.index, sel.length)
    // Clicking an existing link leaves a collapsed selection; expand it to
    // the link's full range so edits/removal apply to the whole link.
    if (format.link && sel.length === 0) {
      const [link, offset] = quill.scroll.descendant(CopCustomLink, sel.index)
      if (link) {
        sel = { index: sel.index - offset, length: link.length() }
        format = quill.getFormat(sel.index, sel.length)
      }
    }
    pendingLinkRef.current = { quillRef, selection: sel }
    const existing = format.link
    setLinkModal({
      show: true,
      url: existing ? (typeof existing === 'string' ? existing : existing.url || '') : '',
      newTab: existing ? (typeof existing === 'object' ? existing.target === '_blank' : false) : false,
    })
  }, [])

  // Route the default link tooltip's "Edit" action into our own modal so
  // editing an existing link also exposes the "Open in new tab" toggle.
  useEffect(() => {
    const cleanups = [userQuillRef, publicQuillRef].map((quillRef) => {
      const tooltip = quillRef.current?.getEditor()?.theme?.tooltip
      const actionEl = tooltip?.root?.querySelector('a.ql-action')
      if (!actionEl) return null
      const handleEditClick = (event) => {
        event.preventDefault()
        event.stopImmediatePropagation()
        tooltip.hide()
        openLinkModal(quillRef)
      }
      actionEl.addEventListener('click', handleEditClick, true)
      return () => actionEl.removeEventListener('click', handleEditClick, true)
    })
    return () => cleanups.forEach((cleanup) => cleanup?.())
  }, [activeTab, openLinkModal])

  userLinkHandlerRef.current = () => openLinkModal(userQuillRef)
  publicLinkHandlerRef.current = () => openLinkModal(publicQuillRef)

  const userQuillModules = useMemo(() => ({
    toolbar: {
      container: TOOLBAR_CONTAINER,
      handlers: { link: () => userLinkHandlerRef.current?.() },
    },
    clipboard: { matchVisual: false },
  }), [])

  const publicQuillModules = useMemo(() => ({
    toolbar: {
      container: TOOLBAR_CONTAINER,
      handlers: { link: () => publicLinkHandlerRef.current?.() },
    },
    clipboard: { matchVisual: false },
  }), [])

  const handleLinkInsert = () => {
    const { quillRef, selection } = pendingLinkRef.current
    const quill = quillRef?.current?.getEditor()
    if (quill && selection) quill.setSelection(selection, 'silent')
    if (quill && linkModal.url.trim()) {
      quill.format('link', { url: linkModal.url.trim(), target: linkModal.newTab ? '_blank' : '_self' })
    }
    setLinkModal({ show: false, url: '', newTab: false })
  }

  const handleLinkRemove = () => {
    const { quillRef, selection } = pendingLinkRef.current
    const quill = quillRef?.current?.getEditor()
    if (quill && selection) {
      quill.setSelection(selection, 'silent')
      quill.format('link', false)
    }
    setLinkModal({ show: false, url: '', newTab: false })
  }

  const handleUserMessageChange = (value, _delta, _source, editor) => {
    if (getPlainTextLength(value) > CHAR_LIMITS[TABS.USER]) {
      const quill = userQuillRef.current?.getEditor()
      if (quill) {
        if (userLastValidDelta.current) {
          quill.setContents(userLastValidDelta.current, 'silent')
        } else {
          quill.setText('', 'silent')
        }
      }
      setUserPasteError(`Pasted text exceeds the ${CHAR_LIMITS[TABS.USER]} character limit and was not added.`)
      return
    }
    setUserPasteError('')
    userLastValidDelta.current = editor.getContents()
    setUserState(prev => ({ ...prev, message: value }))
  }

  const handlePublicMessageChange = (value, _delta, _source, editor) => {
    if (getPlainTextLength(value) > CHAR_LIMITS[TABS.PUBLIC]) {
      const quill = publicQuillRef.current?.getEditor()
      if (quill) {
        if (publicLastValidDelta.current) {
          quill.setContents(publicLastValidDelta.current, 'silent')
        } else {
          quill.setText('', 'silent')
        }
      }
      setPublicPasteError(`Pasted text exceeds the ${CHAR_LIMITS[TABS.PUBLIC]} character limit and was not added.`)
      return
    }
    setPublicPasteError('')
    publicLastValidDelta.current = editor.getContents()
    setPublicState(prev => ({ ...prev, message: value }))
  }

  const handleUserFieldChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value
    setUserState(prev => ({ ...prev, [field]: value }))
  }

  const buildUserPayload = () => {
    const payload = {
      name: SYSPROP_NAMES.USER_ANNOUNCEMENT,
      value: {
        message: toInlineHtml(userState.message),
        dismissable: userState.dismissable,
      },
      desc: 'Announcement bar message',
      client_secret: Constants.CLIENT_SECRET,
    }
    const styles = {}
    if (userState.closeBtnColor) {
      styles.close_btn = { color: userState.closeBtnColor }
    }
    if (userState.bgColor) {
      styles.top_container = { background: userState.bgColor }
    }
    if (Object.keys(styles).length > 0) {
      payload.value.styles = styles
    }
    return payload
  }

  const buildPublicPayload = () => ({
    name: SYSPROP_NAMES.PUBLIC_ANNOUNCEMENT,
    value: [toInlineHtml(publicState.message)],
    desc: 'Announcement bar message',
    client_secret: Constants.CLIENT_SECRET,
  })

  const handleSubmit = () => {
    const message = activeTab === TABS.USER ? userState.message : publicState.message
    if (!message || message === '<p><br></p>') {
      showToastNotification({ message: 'Message cannot be empty', isSuccess: false })
      return
    }
    setShowConfirmModal(true)
  }

  const handleConfirm = () => {
    const isUser = activeTab === TABS.USER
    const body = isUser ? buildUserPayload() : buildPublicPayload()
    setLoading(true)
    ApiHelper(client, 'UPDATE_SYSPROP').API(client, body)
      .then((response) => {
        if (response.status === Constants.CO_API.SUCCESS) {
          showToastNotification({
            message: 'Announcement bar updated successfully',
            isSuccess: true,
          })
          if (isUser) {
            setUserState(initialUserState)
          } else {
            setPublicState(initialPublicState)
          }
        } else {
          showToastNotification({
            message: _.get(response, 'error.message', 'Failed to update announcement bar'),
            isSuccess: false,
          })
        }
      })
      .catch((error) => {
        showToastNotification({
          message: _.get(error, 'response.data.error.message') ||
            _.get(error, 'response.data.message') ||
            'Something went wrong',
          isSuccess: false,
        })
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    const toolbar = document.querySelector('.announcement-editor-section .ql-toolbar')
    if (!toolbar) return
    toolbar.querySelectorAll('button, .ql-picker').forEach((el) => {
      const match = Object.keys(TOOLTIP_MAP).find((cls) => el.classList.contains(cls))
      if (match) el.setAttribute('data-tooltip', TOOLTIP_MAP[match])
    })
  }, [activeTab])

  useEffect(() => {
    setLoadingLive(true)
    Promise.allSettled([
      ApiHelper(client, 'GET_USER_ANNOUNCEMENT').API(client, { name: SYSPROP_NAMES.USER_ANNOUNCEMENT, client_secret: Constants.CLIENT_SECRET }),
      ApiHelper(client, 'GET_PUBLIC_ANNOUNCEMENT').API(client, { name: SYSPROP_NAMES.PUBLIC_ANNOUNCEMENT, client_secret: Constants.CLIENT_SECRET }),
    ]).then(([userResult, publicResult]) => {
      if (userResult.status === 'fulfilled') {
        const ann = _.get(userResult.value, 'data.value', null)
        setCurrentLiveUser(ann)
      }
      if (publicResult.status === 'fulfilled') {
        const raw = _.get(publicResult.value, 'data.value', null)
        const msg = Array.isArray(raw) ? raw[0] : raw
        setCurrentLivePublic(typeof msg === 'string' ? msg : null)
      }
    }).finally(() => setLoadingLive(false))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleLoadLiveUser = () => {
    if (!currentLiveUser) return
    setUserState({
      message: currentLiveUser.message || '',
      dismissable: currentLiveUser.dismissable !== undefined ? currentLiveUser.dismissable : true,
      closeBtnColor: _.get(currentLiveUser, 'styles.close_btn.color', ''),
      bgColor: _.get(currentLiveUser, 'styles.top_container.background', '') ||
        _.get(currentLiveUser, 'styles.top_container.backgroundColor', ''),
    })
    setUserPasteError('')
  }

  const handleLoadLivePublic = () => {
    if (!currentLivePublic) return
    setPublicState({ message: currentLivePublic })
    setPublicPasteError('')
  }

  const isEmptyMessage = (msg) => !msg || msg === '<p><br></p>'
  const currentMessage = activeTab === TABS.USER ? userState.message : publicState.message
  const showPreview = !isEmptyMessage(currentMessage) ||
    (activeTab === TABS.USER && (userState.bgColor || userState.closeBtnColor))

  return (
    <>
      <h1 className='header'>Announcement Bar Update</h1>
      <div className='announcement-bar-wrapper'>
        <p className='heading-subtext'>
          Update sticky note content and improve user experience across the ClearoutPhone app and website
        </p>
        <Nav
          variant='tabs'
          activeKey={activeTab}
          onSelect={setActiveTab}
          className='mb-4'
        >
          <Nav.Item>
            <Nav.Link eventKey={TABS.USER}>App Announcement Message</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey={TABS.PUBLIC}>Website Announcement Message</Nav.Link>
          </Nav.Item>
        </Nav>
        {activeTab === TABS.USER && (
          <>
            <CurrentlyLiveCard
              loading={loadingLive}
              liveData={currentLiveUser}
              tab={TABS.USER}
              onLoad={handleLoadLiveUser}
            />
            <AppAnnouncementMessage
              userState={userState}
              userQuillRef={userQuillRef}
              userQuillModules={userQuillModules}
              handleUserMessageChange={handleUserMessageChange}
              handleUserFieldChange={handleUserFieldChange}
              setUserState={setUserState}
              getPlainTextLength={getPlainTextLength}
              pasteError={userPasteError}
              defaultBgColor={DEFAULT_BG_PICKER_COLOR}
            />
          </>
        )}
        {activeTab === TABS.PUBLIC && (
          <>
            <CurrentlyLiveCard
              loading={loadingLive}
              liveData={currentLivePublic}
              tab={TABS.PUBLIC}
              onLoad={handleLoadLivePublic}
            />
            <WebsiteAnnouncementMessage
              publicState={publicState}
              publicQuillRef={publicQuillRef}
              publicQuillModules={publicQuillModules}
              handlePublicMessageChange={handlePublicMessageChange}
              getPlainTextLength={getPlainTextLength}
              pasteError={publicPasteError}
            />
          </>
        )}
        {showPreview && (
          <div className='announcement-preview-section mt-4'>
            <p
              className='fw-semibold mb-2'
              style={{ fontSize: '14px', color: '#555' }}
            >
              Preview
            </p>
            <div className='announcement-preview-mock-header'>
              <div className='announcement-preview-mock-logo' />
              <div
                className='announcement-preview-bar'
                style={{
                  background: activeTab === TABS.USER
                    ? (userState.bgColor || DEFAULT_BG[TABS.USER])
                    : DEFAULT_BG[TABS.PUBLIC],
                  color: activeTab === TABS.USER ? undefined : '#000000',
                }}
              >
                {!isEmptyMessage(currentMessage) && (
                  <div
                    className='announcement-preview-message'
                    dangerouslySetInnerHTML={{ __html: toInlineHtml(currentMessage) }}
                  />
                )}
                {activeTab === TABS.USER && userState.dismissable && (
                  <span
                    className='announcement-preview-close'
                    style={{ color: userState.closeBtnColor || '#fff' }}
                  >
                    ✕
                  </span>
                )}
              </div>
              <div className='announcement-preview-mock-nav' />
            </div>
            <p className='announcement-preview-hint'>Appears centered in the top navigation bar</p>
          </div>
        )}
        <div className='announcement-bar-submit mt-4'>
          <Button
            variant='outline-success'
            onClick={handleSubmit}
            disabled={loading || isEmptyMessage(currentMessage)}
          >
            {loading ? 'Publishing...' : 'Publish Announcement Bar'}
          </Button>
        </div>
      </div>
      <Modal
        show={linkModal.show}
        onHide={() => setLinkModal(m => ({ ...m, show: false }))}
        centered
        size='sm'
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '16px' }}>Insert Link</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className='mb-3'>
            <Form.Label className='mb-1'>URL</Form.Label>
            <Form.Control
              type='url'
              placeholder='https://example.com'
              value={linkModal.url}
              onChange={(e) => setLinkModal(m => ({ ...m, url: e.target.value }))}
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter' && linkModal.url.trim()) handleLinkInsert() }}
            />
          </Form.Group>
          <Form.Check
            type='switch'
            id='cop-link-modal-new-tab'
            label='Open in new tab'
            checked={linkModal.newTab}
            onChange={(e) => setLinkModal(m => ({ ...m, newTab: e.target.checked }))}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant='outline-danger'
            size='sm'
            onClick={handleLinkRemove}
          >
            Remove
          </Button>
          <Button
            variant='outline-secondary'
            size='sm'
            onClick={() => setLinkModal(m => ({ ...m, show: false }))}
          >
            Cancel
          </Button>
          <Button
            variant='outline-success'
            size='sm'
            onClick={handleLinkInsert}
            disabled={!linkModal.url.trim()}
          >
            Insert
          </Button>
        </Modal.Footer>
      </Modal>
      <ConfirmationModal
        show={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title='Publish Announcement Bar'
        message='Are you sure you want to publish this announcement message?'
        onConfirm={handleConfirm}
        confirmText='Yes, Publish'
        cancelText='No'
      />
    </>
  )
}

export default CopAnnouncementBar
